/* Duha Store — shelves and card films.
   Shelves scroll sideways by touch, trackpad or the two round buttons.
   Card films are silent; they play only while at least half of the card is
   on screen, never for visitors who ask for reduced motion, and each has a
   pause button. */
(() => {
  const rtl = document.documentElement.dir === 'rtl';
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initShelf(shelf) {
    const track = shelf.querySelector('[data-shelf-track]');
    const prev = shelf.querySelector('[data-shelf-prev]');
    const next = shelf.querySelector('[data-shelf-next]');
    if (!track || !prev || !next) return;

    // In right-to-left pages browsers report scrollLeft as 0 … -max.
    const position = () => Math.abs(track.scrollLeft);
    const max = () => track.scrollWidth - track.clientWidth;
    const step = () => {
      const card = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
      const width = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
      return Math.max(width, Math.floor(track.clientWidth / width) * width);
    };
    const update = () => {
      prev.disabled = position() <= 2;
      next.disabled = position() >= max() - 2;
    };
    const go = (dir) => track.scrollBy({ left: dir * step() * (rtl ? -1 : 1), behavior: calm.matches ? 'auto' : 'smooth' });

    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initFilms() {
    const films = [...document.querySelectorAll('.st-card__video')];
    if (!films.length) return;

    films.forEach((video) => {
      video.muted = true;
      video.setAttribute('aria-hidden', 'true');
      const button = video.parentElement.querySelector('[data-video-toggle]');
      const show = (paused) => {
        if (!button) return;
        button.classList.toggle('is-paused', paused);
        button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
      };
      video.addEventListener('play', () => show(false));
      video.addEventListener('pause', () => show(true));
      if (button) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          video.dataset.userPaused = video.paused ? '' : '1';
          video.paused ? video.play() : video.pause();
        });
      }
      show(true);
    });

    if (calm.matches || !('IntersectionObserver' in window)) return;
    const seen = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting && !target.dataset.userPaused) {
          if (target.preload === 'none') target.preload = 'auto';
          target.play().catch(() => {});
        } else if (!isIntersecting) {
          target.pause();
        }
      });
    }, { threshold: 0.5 });
    films.forEach((video) => seen.observe(video));
  }

  /* Global bar: panels open on hover or keyboard focus, a blurred curtain
     covers the page; on phones the menu button opens a full-screen list. */
  function initNav(nav) {
    if (nav.dataset.ready) return;
    nav.dataset.ready = '1';
    let timer;
    const items = [...nav.querySelectorAll('.gn__item')].filter((li) => li.querySelector('[data-gn-flyout]'));
    const wide = window.matchMedia('(min-width: 834px)');
    const close = () => {
      items.forEach((li) => { li.classList.remove('is-open'); li.querySelector('[data-gn-trigger]').setAttribute('aria-expanded', 'false'); });
      nav.classList.remove('has-flyout');
    };
    const open = (li) => {
      clearTimeout(timer);
      items.forEach((other) => other !== li && other.classList.remove('is-open'));
      li.classList.add('is-open');
      li.querySelector('[data-gn-trigger]').setAttribute('aria-expanded', 'true');
      nav.classList.add('has-flyout');
    };
    items.forEach((li) => {
      const trigger = li.querySelector('[data-gn-trigger]');
      if (li.classList.contains('gn__item--panel')) {
        // Search and bag open on click, like Apple's; on phones they are plain links.
        trigger.addEventListener('click', (e) => {
          if (!wide.matches) return;
          e.preventDefault();
          if (li.classList.contains('is-open')) { close(); return; }
          open(li);
          const input = li.querySelector('[data-gn-search-input]');
          if (input) setTimeout(() => input.focus(), 120);
        });
        return;
      }
      li.addEventListener('mouseenter', () => { timer = setTimeout(() => open(li), 120); });
      li.addEventListener('focusin', () => open(li));
    });
    nav.addEventListener('mouseleave', () => {
      clearTimeout(timer);
      if (!nav.querySelector('.gn__item--panel.is-open')) close();
    });
    nav.querySelectorAll('.gn__item').forEach((li) => {
      if (!items.includes(li)) li.addEventListener('mouseenter', () => {
        clearTimeout(timer);
        if (!nav.querySelector('.gn__item--panel.is-open')) close();
      });
    });
    nav.querySelector('[data-gn-curtain]').addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    const burger = nav.querySelector('[data-gn-menu]');
    const panel = nav.querySelector('[data-gn-mobile]');
    if (burger && panel) {
      burger.addEventListener('click', () => {
        const on = burger.getAttribute('aria-expanded') !== 'true';
        burger.setAttribute('aria-expanded', String(on));
        panel.hidden = !on;
        document.documentElement.classList.toggle('is-locked', on);
      });
    }
  }

  /* Footer columns are lists on desktop and fold away on phones. */
  function initFooter() {
    const wide = window.matchMedia('(min-width: 834px)');
    const set = () => document.querySelectorAll('[data-af-col]').forEach((d) => { d.open = wide.matches; });
    set();
    wide.addEventListener('change', set);
  }

  /* Buy page: gallery, films, choices and the sticky price bar. */
  function initBuy(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const track = root.querySelector('[data-ab-track]');
    const slides = [...root.querySelectorAll('[data-ab-slide]')];
    const dots = [...root.querySelectorAll('[data-ab-dot]')];
    const prev = root.querySelector('[data-ab-prev]');
    const next = root.querySelector('[data-ab-next]');
    let index = 0;

    const playIn = (slide) => {
      const video = slide && slide.querySelector('video');
      if (!video) return;
      video.muted = true;
      const replay = slide.querySelector('[data-ab-replay]');
      if (calm.matches) { if (replay) replay.hidden = false; return; }
      video.currentTime = 0;
      video.play().catch(() => { if (replay) replay.hidden = false; });
    };
    const show = (i) => {
      index = Math.max(0, Math.min(slides.length - 1, i));
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === index)));
      if (prev) prev.hidden = index === 0;
      if (next) next.hidden = index === slides.length - 1;
      slides.forEach((s, n) => { const v = s.querySelector('video'); if (v && n !== index) v.pause(); });
      playIn(slides[index]);
    };
    const goTo = (i) => {
      const s = slides[Math.max(0, Math.min(slides.length - 1, i))];
      if (s) track.scrollTo({ left: s.offsetLeft - track.offsetLeft, behavior: calm.matches ? 'auto' : 'smooth' });
    };
    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));
    dots.forEach((d) => d.addEventListener('click', () => goTo(Number(d.dataset.abDot))));
    if (track && 'IntersectionObserver' in window) {
      const seen = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting && e.intersectionRatio > .6) show(slides.indexOf(e.target)); });
      }, { root: track, threshold: [.6] });
      slides.forEach((s) => seen.observe(s));
    }
    root.querySelectorAll('video').forEach((video) => {
      const replay = video.parentElement.querySelector('[data-ab-replay]');
      video.addEventListener('ended', () => { if (replay) replay.hidden = false; });
      video.addEventListener('play', () => { if (replay) replay.hidden = true; });
      if (replay) replay.addEventListener('click', () => { video.currentTime = 0; video.play(); });
    });
    show(0);

    // Choices → the matching variant.
    const data = root.querySelector('[data-ab-variants]');
    const variants = data ? JSON.parse(data.textContent) : [];
    const groups = [...root.querySelectorAll('[data-ab-option]')];
    const idInput = root.querySelector('[data-ab-variant]');
    const button = root.querySelector('[data-ab-button]');
    const money = (cents) => {
      const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.00$/, '');
      const m = (window.Duha && window.Duha.money) || { template: '__C__ __A__', currency: 'AED' };
      return m.template.replace('__A__', amount).replace('__C__', m.currency);
    };
    groups.forEach((g) => g.addEventListener('change', () => {
      const chosen = groups.map((grp) => (grp.querySelector('input:checked') || {}).value);
      const v = variants.find((x) => x.options.every((o, n) => chosen[n] === undefined || o === chosen[n]));
      if (!v) return;
      if (idInput) idInput.value = v.id;
      root.querySelectorAll('.ab-sum [data-ab-price]').forEach((el) => { el.textContent = money(v.price); });
      if (button) {
        button.disabled = !v.available;
        button.textContent = v.available ? button.dataset.labelBuy : button.dataset.labelSold;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(null, '', url);
      if (v.featured_media) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(v.featured_media.id));
        if (i > -1) goTo(i);
      }
    }));

    // The sticky bar shows once the title has scrolled away.
    const bar = document.querySelector('[data-ab-stick]');
    const title = root.querySelector('[data-ab-title]');
    if (bar && title && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        const on = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', String(!on));
      }).observe(title);
    }
  }

  const start = () => {
    document.querySelectorAll('[data-shelf]').forEach(initShelf);
    document.querySelectorAll('[data-gn]').forEach(initNav);
    document.querySelectorAll('[data-ab]').forEach(initBuy);
    initFooter();
    initFilms();
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  document.addEventListener('shopify:section:load', start);
})();
